// The account key hierarchy — the zero-knowledge core.
//
//   master password ─Argon2id(salt)→ master key
//      ├─HKDF("enc")→ stretched key ─(AES-GCM)→ wraps → user symmetric key (random)
//      └─HKDF("auth")→ auth key → sent at sign-in; the server stores a peppered hash
//
//   user symmetric key ─encrypts→ vault items
//                      ─wraps→ the user's RSA private key
//
// The server only ever stores the `ProtectedAccount` blobs below. None of them
// is usable without the master password, which never leaves the browser.
// Rotating the master password only re-wraps the user symmetric key — the vault
// itself is never re-encrypted.

import {
  aesDecrypt,
  aesEncrypt,
  exportPrivateKeyPkcs8,
  exportPublicKey,
  generateRsaKeyPair,
  importAesKey,
  importPrivateKey,
} from "./cipher";
import { fromB64, randomBytes, toB64, type Bytes } from "./encoding";
import {
  DEFAULT_KDF_PARAMS,
  deriveMasterKey,
  deriveSubBytes,
  deriveSubKey,
  type KdfParams,
} from "./kdf";

const KDF_SALT_BYTES = 16;
const USER_KEY_BYTES = 32;
const ENC_INFO = "enc"; // HKDF domain-separation label for the stretched key
const AUTH_INFO = "auth"; // HKDF label for the server sign-in key

/** Live, in-memory keys after a successful unlock. Never persisted. */
export interface AccountKeys {
  userKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyB64: string;
}

/** The opaque, at-rest key material the server stores. Useless without the password. */
export interface ProtectedAccount {
  kdf: KdfParams;
  kdfSaltB64: string;
  protectedSymmetricKey: string; // user key, wrapped by the stretched master key
  publicKeyB64: string;
  protectedPrivateKey: string; // RSA private key, wrapped by the user key
}

/** What one Argon2id run over the master password yields. */
export interface DerivedKeys {
  stretched: CryptoKey; // unwraps the user key; never leaves the browser
  authKeyB64: string; // proves the password to the server at sign-in
}

/** Run the KDF once and split it into the encryption and auth subkeys. */
export async function deriveKeys(
  password: string,
  saltB64: string,
  params: KdfParams,
): Promise<DerivedKeys> {
  const salt = fromB64(saltB64);
  const masterKey = await deriveMasterKey(password, salt, params);
  const [stretched, auth] = await Promise.all([
    deriveSubKey(masterKey, salt, ENC_INFO),
    deriveSubBytes(masterKey, salt, AUTH_INFO),
  ]);
  return { stretched, authKeyB64: toB64(auth) };
}

async function stretchedKeyFor(
  password: string,
  saltB64: string,
  params: KdfParams,
): Promise<CryptoKey> {
  return (await deriveKeys(password, saltB64, params)).stretched;
}

/**
 * Onboarding: create a brand-new account from a master password.
 * Returns the at-rest `protected` blobs (for the server) and the live `keys`
 * (kept in memory for this session).
 */
export async function createAccount(
  password: string,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<{ protected: ProtectedAccount; keys: AccountKeys; authKeyB64: string }> {
  const kdfSaltB64 = toB64(randomBytes(KDF_SALT_BYTES));
  const { stretched, authKeyB64 } = await deriveKeys(password, kdfSaltB64, params);

  // Random symmetric key — the root of all this user's data encryption.
  const userKeyRaw = randomBytes(USER_KEY_BYTES);
  const userKey = await importAesKey(userKeyRaw);
  const protectedSymmetricKey = await aesEncrypt(stretched, userKeyRaw);

  // Asymmetric keypair for shared-vault key exchange (used from Phase 4).
  const pair = await generateRsaKeyPair();
  const publicKeyB64 = await exportPublicKey(pair.publicKey);
  const protectedPrivateKey = await aesEncrypt(
    userKey,
    await exportPrivateKeyPkcs8(pair.privateKey),
  );

  return {
    protected: {
      kdf: params,
      kdfSaltB64,
      protectedSymmetricKey,
      publicKeyB64,
      protectedPrivateKey,
    },
    keys: { userKey, privateKey: pair.privateKey, publicKeyB64 },
    authKeyB64,
  };
}

/**
 * Unlock an existing account. Throws if the master password is wrong — the
 * AES-GCM auth tag on the wrapped user key won't verify under the wrong key.
 */
export async function unlockAccount(
  password: string,
  acct: ProtectedAccount,
): Promise<AccountKeys> {
  return unlockWithStretched(
    await stretchedKeyFor(password, acct.kdfSaltB64, acct.kdf),
    acct,
  );
}

/** Unlock with a stretched key already derived during sign-in (one KDF run). */
export async function unlockWithStretched(
  stretched: CryptoKey,
  acct: ProtectedAccount,
): Promise<AccountKeys> {
  let userKeyRaw: Bytes;
  try {
    userKeyRaw = await aesDecrypt(stretched, acct.protectedSymmetricKey);
  } catch {
    throw new WrongPasswordError();
  }
  const userKey = await importAesKey(userKeyRaw);

  const privateKey = await importPrivateKey(
    await aesDecrypt(userKey, acct.protectedPrivateKey),
  );

  return { userKey, privateKey, publicKeyB64: acct.publicKeyB64 };
}

/**
 * Rotate the master password without re-encrypting the vault: re-wrap the same
 * user key under a key stretched from the new password. Requires the account to
 * be unlocked (so we have the live user key).
 */
export async function changeMasterPassword(
  newPassword: string,
  keys: AccountKeys,
  current: ProtectedAccount,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<ProtectedAccount & { authKeyB64: string }> {
  const kdfSaltB64 = toB64(randomBytes(KDF_SALT_BYTES));
  const { stretched, authKeyB64 } = await deriveKeys(newPassword, kdfSaltB64, params);
  const userKeyRaw = await crypto.subtle.exportKey("raw", keys.userKey);
  const protectedSymmetricKey = await aesEncrypt(
    stretched,
    new Uint8Array(userKeyRaw),
  );
  return {
    ...current,
    kdf: params,
    kdfSaltB64,
    protectedSymmetricKey,
    authKeyB64,
  };
}

export class WrongPasswordError extends Error {
  constructor() {
    super("incorrect master password");
    this.name = "WrongPasswordError";
  }
}
